// The machine details panel as pure data (#6563, prototype `panel()`): which machine the
// url's `?machine=` names, who may act on it, what the diagnosis says, the readout words,
// whether its build is up to date and the drain action's words (the card menu's items are
// `cardMenu`, `./machine-card`). `<MachinePanel/>` renders it; nothing here reads a clock, a
// translator or the url, so every rule unit-tests on a fixture.
//
// EVERY JUDGEMENT IS THE SERVER'S. The panel reads `verdict` (#6557): its status, health
// and per-resource signal tiers. A word here is a tier's name, never a re-graded reading.

import {
  FLEET_UPDATE_MATURITY_MARGIN_MS,
  FLEET_UPDATE_PUSH_WORST_CASE_MS,
  type MachineSignalLevel,
  type RunnerRole,
  runnerReleaseTargetForArch,
} from '@developerz/domain';
import type { ReleaseRow } from './box-lifecycle';
import type { UtilBox } from './fleetUtilization';
import type { ResourceKind } from './machine-bars';

/**
 * Where an open lands (#6563 v2, item 1; `rename` is #6566's: the name's inline form, open
 * and focused). Not in the url: `?machine=` is the one param.
 */
export type PanelTarget = 'top' | 'instructions' | 'remove' | 'rename';

/** The url parameter that opens the panel (owner ruling G10). */
export const MACHINE_PARAM = 'machine';

/**
 * What `?machine=<id>` resolves to. `loading` is a param read before the first
 * utilization answer: a cold deep link must not flash "removed or not in this org" for a
 * machine the read has not returned yet.
 */
export type PanelVerdict =
  | { readonly kind: 'closed' }
  | { readonly kind: 'loading'; readonly id: string }
  | { readonly kind: 'open'; readonly box: UtilBox }
  | { readonly kind: 'missing'; readonly id: string };

export function panelVerdict(
  id: string | null,
  boxes: readonly UtilBox[] | undefined,
): PanelVerdict {
  if (id === null || id === '') return { kind: 'closed' };
  if (boxes === undefined) return { kind: 'loading', id };
  const box = boxes.find((b) => b.id === id);
  return box === undefined ? { kind: 'missing', id } : { kind: 'open', box };
}

/**
 * Who may act on this machine here. An admin of this org on its own machine gets the
 * Actions (owner ruling G21, `canAdminWrite`); a member or viewer gets none; a machine
 * from another org is read-only for everyone and is managed in that org (G23).
 */
export type MachineAccess =
  | { readonly kind: 'admin' }
  | { readonly kind: 'member' }
  | { readonly kind: 'other_org'; readonly org: string };

export function machineAccess(box: UtilBox, canWrite: boolean): MachineAccess {
  if (box.via_org !== null) return { kind: 'other_org', org: box.via_org };
  return canWrite ? { kind: 'admin' } : { kind: 'member' };
}

/**
 * The diagnosis section. `overloaded` names the CPU ratio, whether memory and disk are
 * both fine (the prototype says "Memory and disk are fine." whatever they read, one of
 * its bugs), and the server when another machine on it is overloaded too: then the server
 * itself is short on processors. `attention` is any other health that needs a look;
 * `fine` is every reading within limits; `none` is a machine with no readings to judge.
 */
export type MachineDiagnosis =
  | {
      readonly kind: 'overloaded';
      readonly ratio: number | null;
      readonly othersFine: boolean;
      readonly server: string | null;
    }
  | { readonly kind: 'attention' }
  | { readonly kind: 'fine' }
  | { readonly kind: 'none' };

export function machineDiagnosis(box: UtilBox, boxes: readonly UtilBox[]): MachineDiagnosis {
  const { health, signals, needs_attention } = box.verdict;
  if (health === 'overloaded') {
    const shared = boxes.some(
      (o) =>
        o.id !== box.id && o.server.key === box.server.key && o.verdict.health === 'overloaded',
    );
    return {
      kind: 'overloaded',
      ratio: signals.cpu.ratio,
      othersFine: signals.memory.level === 'ok' && signals.disk.level === 'ok',
      server: shared ? (box.server.label ?? box.server.address) : null,
    };
  }
  if (needs_attention) return { kind: 'attention' };
  const levels = [signals.cpu.level, signals.memory.level, signals.disk.level];
  if (levels.every((l) => l === 'ok')) return { kind: 'fine' };
  return { kind: 'none' };
}

/**
 * A readout's word per resource and tier (the prototype's `cpuWord` / `memWord` /
 * `diskWord`). Healthy is neutral, never green.
 */
export const READOUT_WORD: Readonly<
  Record<ResourceKind, Readonly<Record<MachineSignalLevel, string>>>
> = {
  cpu: {
    ok: 'fleet.panel.word_healthy',
    warn: 'fleet.panel.word_busy',
    crit: 'fleet.panel.word_overloaded',
    not_reported: 'fleet.panel.word_not_reported',
  },
  memory: {
    ok: 'fleet.panel.word_healthy',
    warn: 'fleet.panel.word_tight',
    crit: 'fleet.panel.word_critical',
    not_reported: 'fleet.panel.word_not_reported',
  },
  disk: {
    ok: 'fleet.panel.word_healthy',
    warn: 'fleet.panel.word_low',
    crit: 'fleet.panel.word_critical',
    not_reported: 'fleet.panel.word_not_reported',
  },
};

/**
 * "Up to date": the machine runs the newest non-yanked `stable` build for its arch target
 * that has matured past the push's worst case plus the margin (`FLEET_UPDATE_*`,
 * `@developerz/domain` fleet-update.ts), the build the release-drift lane treats as the
 * fleet's. A newer release still inside that window is one the fleet is not meant to be
 * on yet. No arch (no specs) or no version: no badge, never a guess.
 */
export function upToDate(input: {
  readonly version: string | null;
  readonly arch: string | null;
  readonly releases: readonly ReleaseRow[];
  readonly nowMs: number;
}): boolean {
  const target = runnerReleaseTargetForArch(input.arch);
  if (target === undefined || input.version === null) return false;
  const maturedBy = input.nowMs - FLEET_UPDATE_PUSH_WORST_CASE_MS - FLEET_UPDATE_MATURITY_MARGIN_MS;
  let newest: { version: string; at: number } | null = null;
  for (const row of input.releases) {
    if (row.channel !== 'stable' || row.target !== target) continue;
    if ((row.yanked_at ?? null) !== null) continue;
    const at = Date.parse(row.published_at);
    if (!Number.isFinite(at) || at > maturedBy) continue;
    if (newest === null || at > newest.at) newest = { version: row.version, at };
  }
  return newest !== null && newest.version === input.version;
}

/** What the machine "Can do", by its pinned role. Other roles take no queued work. */
export function canDoKey(mode: RunnerRole): string | null {
  if (mode === 'worker') return 'fleet.panel.can_worker';
  if (mode === 'universal') return 'fleet.panel.can_universal';
  return null;
}

/**
 * The drain action (#6563 v2, item 3): "Stop taking new work" drains, "Start taking new
 * work" undrains. Off the machine's real status (`paused` is drained), never a
 * click-tracked flag. A machine still waiting to connect has nothing to stop.
 */
export function drainAction(
  box: UtilBox,
): { readonly label: string; readonly desc: string; readonly resume: boolean } | null {
  const status = box.verdict.status;
  if (status === 'waiting_to_connect') return null;
  return status === 'paused'
    ? { label: 'fleet.machines.menu_resume', desc: 'fleet.panel.resume_desc', resume: true }
    : { label: 'fleet.machines.menu_pause', desc: 'fleet.panel.pause_desc', resume: false };
}
